import type { Time } from "@suse-o11y/date-time/time";
import { IconAssistant, IconPause } from "@suse-o11y/icons";
import React, { type SetStateAction, useCallback, useEffect } from "react";

import type { BorgAgentType, BorgMessage } from "../models/borg-types";

import type { ContextBarConfig } from "./_context-bar";
import type { UseConversationLifecycleResult } from "./_conversation-lifecycle-types";
import { useContextBarState } from "./_use-context-bar-state";
import { useContextPins } from "./_use-context-pins";
import { useConversationLifecycle } from "./_use-conversation-lifecycle";
import { useNavigateToComponent } from "./_use-navigate-to-component";
import type { AiAssistantInput } from "./build-question";
import { getLastConversationId, setLastConversationId } from "./drawer-memory";
import { HistoryView } from "./history-panel";
import { type LifecycleResultLike, SessionContentLayout } from "./session-content-layout";

export interface ExportData {
	question?: string;
	startedAt?: Time;
	messages?: ReadonlyArray<BorgMessage>;
}

export interface FooterInputConfig {
	show: boolean;
	disabled: boolean;
	placeholder: string;
	showModeSelector: boolean;
	showStop: boolean;
	stopLabel?: string;
}

const SUGGESTIONS = [
	"What components in my environment are unhealthy?",
	"Show me recent critical health state changes",
	"Are there any anomalies in my infrastructure?",
];

const INVESTIGATION_SHORTCUT = "Investigate what's wrong in my current view";

const WelcomeState: React.FC<{
	onInvestigate: () => void;
	onSubmit: (question: string) => void;
}> = ({ onInvestigate, onSubmit }) => (
	<div className="flex flex-1 flex-col items-center justify-center gap-6 px-4">
		<div className="flex flex-col items-center gap-3">
			<div className="flex size-26 items-center justify-center rounded-full border border-grey-300 bg-white shadow-tooltip">
				<IconAssistant className="size-12" />
			</div>
			<div className="text-center">
				<p className="font-semibold text-grey-900 text-lg">I'm Liz, your AI assistant</p>
				<p className="text-grey-600 text-sm">Ask me anything about your environment</p>
			</div>
		</div>

		<div className="w-full max-w-sm rounded-xl border border-grey-300 bg-grey-100 p-4 shadow-tooltip">
			<p className="mb-3 font-medium text-grey-900 text-sm">Quick start</p>
			<button
				className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-grey-400 bg-white px-3 py-2.5 text-left text-grey-900 text-sm shadow-sm transition-colors hover:border-primary-500 hover:text-primary-600"
				onClick={onInvestigate}
				type="button"
			>
				<IconPause className="size-4 rotate-90 text-primary-600" />
				<span>{INVESTIGATION_SHORTCUT}</span>
			</button>
		</div>

		<div className="w-full max-w-sm rounded-xl border border-grey-300 bg-white p-4 shadow-tooltip">
			<p className="mb-3 font-medium text-grey-700 text-sm">Try asking:</p>
			<div className="flex flex-col gap-2">
				{SUGGESTIONS.map((suggestion) => (
					<button
						className="cursor-pointer rounded-lg border border-grey-300 bg-grey-100 px-3 py-2 text-left text-grey-700 text-sm transition-colors hover:border-primary-500 hover:bg-white hover:text-primary-600"
						key={suggestion}
						onClick={() => onSubmit(suggestion)}
						type="button"
					>
						{suggestion}
					</button>
				))}
			</div>
		</div>
	</div>
);

interface ChatDrawerContentProps {
	content: AiAssistantInput;
	exportDataRef: React.MutableRefObject<ExportData | null>;
	historyOpen: boolean;
	onHistoryClose: () => void;
	onNewConversation: () => void;
	onResend: (text: string) => void;
	selectedMode: BorgAgentType;
	setContextBarConfig: React.Dispatch<SetStateAction<ContextBarConfig>>;
	setFooterConfig: React.Dispatch<SetStateAction<FooterInputConfig>>;
	setHasExportableContent: React.Dispatch<SetStateAction<boolean>>;
	stopRef: React.MutableRefObject<() => void>;
	submitRef: React.MutableRefObject<(text: string) => void>;
}

type ActiveConversationLifecycle = Exclude<UseConversationLifecycleResult, { phase: "idle" }>;

const toLayoutLifecycle = (lifecycle: ActiveConversationLifecycle): LifecycleResultLike => ({
	...lifecycle,
	submitFollowUp: lifecycle.sendMessage,
});

const useChatDrawerWiring = (content: AiAssistantInput, onNewConversation: () => void) => {
	const collectedPins = useContextPins(content);
	const contextBarState = useContextBarState(collectedPins);
	const lifecycle = useConversationLifecycle({
		onStaleSession: () => {
			setLastConversationId(undefined);
		},
	});

	// Track conversation ID for persistence
	useEffect(() => {
		if (lifecycle.conversationId != null) {
			setLastConversationId(lifecycle.conversationId);
		}
	}, [lifecycle.conversationId]);

	const { loadExisting } = lifecycle;
	const contentIdentity = content._kind === "existing" ? `existing:${content.sessionId}` : "chat";

	// Load or start on mount
	useEffect(() => {
		if (contentIdentity.startsWith("existing:")) {
			const id = contentIdentity.slice("existing:".length);
			loadExisting(id);
		} else {
			// Resume last conversation if available
			const lastId = getLastConversationId();
			if (lastId != null) {
				loadExisting(lastId);
			}
		}
	}, [contentIdentity, loadExisting]);

	const handleNavigateToComponent = useNavigateToComponent();

	const { reset: resetLifecycle } = lifecycle;

	const handleSelect = useCallback(
		(id: string) => {
			loadExisting(id);
			setLastConversationId(id);
		},
		[loadExisting],
	);

	const { resetRemoved } = contextBarState;
	const handleNewConv = useCallback(() => {
		setLastConversationId(undefined);
		resetLifecycle();
		resetRemoved();
		onNewConversation();
	}, [resetLifecycle, resetRemoved, onNewConversation]);

	return { lifecycle, contextBarState, handleNavigateToComponent, handleSelect, handleNewConv };
};

export const ChatDrawerContent: React.FC<ChatDrawerContentProps> = ({
	content,
	exportDataRef,
	historyOpen,
	onHistoryClose,
	onNewConversation,
	onResend,
	selectedMode,
	setContextBarConfig,
	setFooterConfig,
	setHasExportableContent,
	stopRef,
	submitRef,
}) => {
	const { lifecycle, contextBarState, handleNavigateToComponent, handleSelect, handleNewConv } = useChatDrawerWiring(
		content,
		onNewConversation,
	);

	// Sync context bar config to parent for rendering in footer
	useEffect(() => {
		setContextBarConfig({
			pins: contextBarState.activePins,
			onRemovePin: contextBarState.removePin,
			onAddPin: contextBarState.addPin,
			removedPins: contextBarState.removedPins,
		});
	}, [
		contextBarState.activePins,
		contextBarState.removePin,
		contextBarState.addPin,
		contextBarState.removedPins,
		setContextBarConfig,
	]);

	// Sync submit ref — intentionally has no dependency array so the closure always captures
	// the latest selectedMode, lifecycle.phase, lifecycle.start, and lifecycle.sendMessage.
	// Adding deps would risk stale closure bugs in the branching logic below.
	useEffect(() => {
		submitRef.current = (text: string) => {
			const { activePins } = contextBarState;
			const pins = activePins.length > 0 ? [...activePins] : undefined;
			if (lifecycle.phase === "idle") {
				// For new conversations, "adaptive" is the default — no need to send it.
				const agent = selectedMode === "adaptive" ? undefined : selectedMode;
				lifecycle.start(text, agent, pins);
			} else {
				// For follow-ups, always send the selected mode so the backend
				// knows whether to stick with the current agent or re-route.
				lifecycle.sendMessage(text, selectedMode, pins);
			}
		};
	});

	const { cancelCurrentTurn } = lifecycle;

	useEffect(() => {
		stopRef.current = () => {
			cancelCurrentTurn();
		};
	}, [cancelCurrentTurn, stopRef]);

	// Sync footer config — uses functional update to skip re-render when values are unchanged
	useEffect(() => {
		const { show, disabled, placeholder } = lifecycle.footerInput;
		const isInterruptible =
			lifecycle.phase === "creating" || lifecycle.phase === "polling" || lifecycle.phase === "submitting";
		const showModeSelector = lifecycle.phase !== "idle" || show;
		const stopLabel = lifecycle.phase === "creating" ? "Stop starting" : "Stop response";
		setFooterConfig((prev) => {
			if (
				prev.show === show &&
				prev.disabled === disabled &&
				prev.placeholder === placeholder &&
				prev.showModeSelector === showModeSelector &&
				prev.showStop === isInterruptible &&
				prev.stopLabel === (isInterruptible ? stopLabel : undefined)
			) {
				return prev;
			}
			return {
				show,
				disabled,
				placeholder,
				showModeSelector,
				showStop: isInterruptible,
				stopLabel: isInterruptible ? stopLabel : undefined,
			};
		});
	}, [lifecycle.footerInput, lifecycle.phase, setFooterConfig]);

	// Sync export data for transcript download
	useEffect(() => {
		const hasContent = lifecycle.messages.length > 0;
		exportDataRef.current = hasContent ? { startedAt: lifecycle.startedAt, messages: lifecycle.messages } : null;
		setHasExportableContent((prev) => (prev === hasContent ? prev : hasContent));
	}, [lifecycle.messages, lifecycle.startedAt, exportDataRef, setHasExportableContent]);

	if (historyOpen) {
		return (
			<HistoryView
				activeConversationId={lifecycle.conversationId}
				onNewConversation={() => {
					handleNewConv();
					onHistoryClose();
				}}
				onSelectConversation={(id) => {
					handleSelect(id);
					onHistoryClose();
				}}
			/>
		);
	}

	if (lifecycle.phase === "idle") {
		return (
			<WelcomeState
				onInvestigate={() => {
					submitRef.current(INVESTIGATION_SHORTCUT);
				}}
				onSubmit={(text) => submitRef.current(text)}
			/>
		);
	}

	const sessionLifecycle = toLayoutLifecycle(lifecycle);

	return (
		<SessionContentLayout
			lifecycle={sessionLifecycle}
			onNavigateToComponent={handleNavigateToComponent}
			onResend={onResend}
			onRetrySend={lifecycle.retrySendMessage}
		/>
	);
};
